/**
 * Регистрация PWA-сервис-воркера + хук на событие `beforeinstallprompt`.
 *
 * Поведение:
 * - в production регистрируется `/sw.js` (precache + cache-first для статики);
 * - в dev-режиме (port 5173 от Vite) сервис-воркер НЕ регистрируется, чтобы
 *   HMR не конфликтовал с кэшем; при наличии уже зарегистрированного воркера
 *   выполняется попытка `unregister`, чтобы старый кэш не подменял свежие
 *   модули.
 * - обработчик `beforeinstallprompt` запоминает событие, чтобы UI («Настройки»
 *   → «Установить как приложение») мог триггерить установку.
 */

let deferredInstallPrompt: any = null;
let installAvailableSubscriber: ((available: boolean) => void) | null = null;

function isDevHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  // Vite dev по умолчанию на 5173.
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) && window.location.port !== "" && window.location.port !== "80" && window.location.port !== "443";
}

export function initPwa(): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }
  if (isDevHost()) {
    // В dev-режиме отключаем service worker, чтобы не мешал HMR Vite.
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((reg) => {
        if (reg.scope && reg.scope.includes(window.location.host)) {
          reg.unregister().catch(() => {});
        }
      });
    });
    return;
  }
  window.addEventListener("load", () => {
    // SW лежит рядом с index.html в директории base (см. vite.config / --base):
    // в dev это `/sw.js`, на встроенной раздаче — `/tools/viewer-ifc/sw.js`.
    const swUrl = `${import.meta.env.BASE_URL}sw.js`;
    navigator.serviceWorker.register(swUrl).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[pwa] service worker register failed:", err);
    });
  });

  window.addEventListener("beforeinstallprompt", (event: Event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    installAvailableSubscriber?.(true);
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installAvailableSubscriber?.(false);
  });
}

export function isPwaInstallAvailable(): boolean {
  return Boolean(deferredInstallPrompt);
}

export function onPwaInstallAvailability(cb: (available: boolean) => void): void {
  installAvailableSubscriber = cb;
  cb(isPwaInstallAvailable());
}

export async function promptPwaInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredInstallPrompt) {
    return "unavailable";
  }
  try {
    deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    installAvailableSubscriber?.(false);
    return choice?.outcome === "accepted" ? "accepted" : "dismissed";
  } catch (_) {
    deferredInstallPrompt = null;
    installAvailableSubscriber?.(false);
    return "dismissed";
  }
}
